import { useMemo, useState } from 'react';
import { collapseUnchanged, diffLines, isGap } from './diff';
import type { PromptLanguage, PromptVersion } from './types';

interface Props {
  /** What the prompt is running on right now — the published version, or the compiled seed. */
  baseline: { label: string; en: string; es: string };
  version: PromptVersion;
  onClose: () => void;
}

const ROW_SIGN: Record<string, string> = {
  added: '+',
  removed: '-',
  context: ' ',
};

/**
 * What this version would change about the prompt in service.
 *
 * Without it, comparing a draft against what is live meant reading two text
 * areas side by side and trusting your eyes — and `chat.dr-lynkia-system` is
 * ~6.8 KB per language. The failure that matters is a rule quietly dropped
 * during an edit, which is exactly what eyes are worst at catching.
 *
 * Unchanged stretches collapse to a labelled gap rather than being dropped
 * silently, so the diff never implies the prompt is shorter than it is.
 */
export function VersionDiff({ baseline, version, onClose }: Props) {
  const [language, setLanguage] = useState<PromptLanguage>('en');
  const [showAll, setShowAll] = useState(false);

  const result = useMemo(
    () => diffLines(baseline[language], version[language]),
    [baseline, version, language],
  );

  const rows = useMemo(
    () => (showAll ? result.rows : collapseUnchanged(result.rows)),
    [result.rows, showAll],
  );

  const unchanged = result.added === 0 && result.removed === 0;

  return (
    <div className="form-card full-width" data-testid="prompt-version-diff">
      <div className="ps-card-head">
        <div style={{ minWidth: 0 }}>
          <h2>Compare</h2>
          <p className="ps-card-note">
            <strong>{version.label}</strong> vs {baseline.label}
          </p>
          <p className="ps-diff-stats" style={{ marginTop: '0.35rem' }}>
            {unchanged ? (
              'Identical — this version would change nothing.'
            ) : (
              <>
                <span className="ps-diff-add">+{result.added} added</span>
                <span className="ps-diff-del">-{result.removed} removed</span>
                {result.truncated && <span>too large to align, shown as a full replacement</span>}
              </>
            )}
          </p>
        </div>

        <div className="ps-actions">
          <div className="ps-seg">
            {(['en', 'es'] as const).map((code) => (
              <button
                key={code}
                type="button"
                onClick={() => setLanguage(code)}
                aria-pressed={language === code}
                data-testid={`prompt-diff-lang-${code}`}
                className={language === code ? 'active' : undefined}
              >
                {code.toUpperCase()}
              </button>
            ))}
          </div>
          <button type="button" onClick={onClose} className="btn btn-secondary">
            Close
          </button>
        </div>
      </div>

      {!unchanged && (
        <div className="ps-diff-scroll">
          <table className="ps-diff-table">
            <tbody>
              {rows.map((row, index) => {
                if (isGap(row)) {
                  return (
                    <tr key={`gap-${index}`} className="ps-diff-gap">
                      <td colSpan={3}>
                        ⋯ {row.hidden} unchanged line{row.hidden === 1 ? '' : 's'}
                      </td>
                    </tr>
                  );
                }

                return (
                  <tr key={index} className={`ps-diff-row ${row.type}`}>
                    <td className="ps-diff-num">{row.leftLine ?? ''}</td>
                    <td className="ps-diff-num">{row.rightLine ?? ''}</td>
                    <td className="ps-diff-text">
                      <span className="ps-diff-sign">{ROW_SIGN[row.type]} </span>
                      {row.text || ' '}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!unchanged && !result.truncated && (
        <div className="ps-actions">
          <button
            type="button"
            onClick={() => setShowAll((v) => !v)}
            data-testid="prompt-diff-toggle-context"
            className="btn btn-secondary"
          >
            {showAll ? 'Collapse unchanged lines' : 'Show the whole prompt'}
          </button>
        </div>
      )}
    </div>
  );
}
